import type { IntlShape } from 'react-intl';
import type { ChatItem, ToolUseItem } from '../../../contracts/harness.types';
import type { ObjectCreationKind } from '../../../contracts/projects/object-creation.types';
import { readToolResultJsonCandidates } from '../../../contracts/toolResult';
import {
  deriveInitials,
  deriveObjectSubtitle,
  objectKindForTool,
  titleFromRecord,
} from '../timeline/object-card/objectToolMapping';
import { artifactCanvasKey } from './artifactCanvasKey';
import { artifactPanelMessages } from './artifactPanel.messages';
import type { Artifact, CreatedObjectRef } from './state';

// Creation receipts only. Reads, edits, uploads, pinning and open_artifact are
// context, even when they can open the same canvas destination.
const CREATION_TOOLS = new Set([
  'create_session',
  'create_session_for_member',
  'create_patient_profile',
  'create_session_document',
  'generate_note_from_template',
  'generate_prechart_document',
  'create_session_context_document',
  'add_session_context_text',
  'create_letter',
  'create_clinical_task',
  'create_template',
  'generate_pptx',
  'save_memory',
  'evidence.save_work_output',
  'create_workflow',
  'create_routine',
  'create_scheduled_workflow',
  'create_scheduled_routine',
  'export_to_pdf',
  'export_to_word',
  'generate_word',
  'generate_csv',
  'generate_excel',
  'fill_excel_template',
  'fill_word_form',
  'create_patient_evidence_pdf',
  'fill_pdf_form',
  'fill_form_from_upload',
]);
// The presentation registry has no export classification, so this list and the
// `document-export` target below are the collector's own.
const EXPORT_TOOLS = new Set([
  'export_to_pdf',
  'export_to_word',
  'generate_word',
  'generate_csv',
  'generate_excel',
  'fill_excel_template',
  'fill_word_form',
  'create_patient_evidence_pdf',
  'fill_pdf_form',
  'fill_form_from_upload',
  'generate_pptx',
]);
const TITLE_KEYS = ['title', 'name', 'template_name', 'filename', 'workflow_name'];

/** App-supplied resolvers, so the collector stays headless. */
export interface CreatedChatArtifactPorts {
  resolveArtifact: (item: ToolUseItem) => Artifact | undefined;
  resolveObjectTitle: (
    item: ToolUseItem,
    kind: ObjectCreationKind,
    deps: {
      intl: IntlShape;
      items: ChatItem[];
      resolveArtifact: CreatedChatArtifactPorts['resolveArtifact'];
    }
  ) => string;
  readExportPayload: (item: ToolUseItem) => {
    url?: string;
    title?: string;
    filename?: string;
  };
  resolveExportFilename: (payload: {
    filename?: string;
    title?: string;
    url?: string;
  }) => string;
  isLetterArtifactEnabled: boolean;
}

interface CollectorOptions extends CreatedChatArtifactPorts {
  intl: IntlShape;
}

const resultString = (item: ToolUseItem, ...keys: string[]): string | undefined => {
  for (const record of readToolResultJsonCandidates(item)) {
    const value = titleFromRecord(record, keys);
    if (value) return value;
  }
  return undefined;
};

const createdTarget = (
  item: ToolUseItem,
  options: CollectorOptions
): Artifact | undefined => {
  if (item.name === 'create_letter' && !options.isLetterArtifactEnabled) return undefined;
  if (item.name === 'create_clinical_task') {
    // XXX: the task tools open no preview (AGE-2417), but the Created row still lists the
    // session's tasks when the clinician clicks it.
    const sessionId = item.input.session_id;
    return typeof sessionId === 'string' && sessionId
      ? { type: 'tasks', sessionId }
      : undefined;
  }
  if (item.name === 'create_template') {
    const templateId = resultString(item, 'template_id');
    return templateId ? { type: 'template-builder', templateId } : undefined;
  }
  const target = options.resolveArtifact(item);
  if (!EXPORT_TOOLS.has(item.name) || target?.type === 'presentation') return target;
  // Match the transcript's export preview, including the PPTX download fallback
  // when the Slide Builder gate is off. Never persist the signed URL.
  const payload = options.readExportPayload(item);
  if (!payload.url) return target;
  const filename = options.resolveExportFilename(payload);
  return {
    type: 'document-export',
    url: payload.url,
    filename,
    title: payload.title ?? filename,
    ...(['fill_pdf_form', 'fill_form_from_upload'].includes(item.name)
      ? { forcePdfExtension: true }
      : {}),
  };
};

const fallbackTitle = (intl: IntlShape, target: Artifact): string => {
  if (target.type === 'tasks')
    return intl.formatMessage(artifactPanelMessages.tasksTitle);
  if (target.type === 'letter')
    return intl.formatMessage(artifactPanelMessages.letterTitle);
  return intl.formatMessage({
    defaultMessage: 'Untitled',
    id: 's0pGPawdZs',
    description: 'Row title for a created object whose name could not be resolved',
  });
};

const resolveRowTitle = (
  item: ToolUseItem,
  target: Artifact,
  kind: ObjectCreationKind | undefined,
  items: ChatItem[],
  options: CollectorOptions
): string => {
  if (kind) {
    return options.resolveObjectTitle(item, kind, {
      intl: options.intl,
      items,
      resolveArtifact: options.resolveArtifact,
    });
  }
  if (target.type === 'document-export') return target.title;
  return (
    resultString(item, ...TITLE_KEYS) ??
    titleFromRecord(item.input, TITLE_KEYS) ??
    fallbackTitle(options.intl, target)
  );
};

const objectIdentity = (target: Artifact): string => {
  // A generated session document and a standalone document viewer identify the
  // same object, while the session itself remains a separate creation.
  if (target.type === 'session' && target.focusDocumentId) {
    return `document:${target.sessionId}:${target.focusDocumentId}`;
  }
  return artifactCanvasKey(target);
};

/** Derive display-only creation receipts from hydrated or live chat history. */
export const collectCreatedChatArtifacts = (
  items: ChatItem[],
  options: CollectorOptions
): CreatedObjectRef[] => {
  const objects = new Map<string, CreatedObjectRef>();
  for (const item of items) {
    if (
      item.kind !== 'tool_use' ||
      item.status !== 'complete' ||
      item.result?.is_error ||
      !CREATION_TOOLS.has(item.name)
    )
      continue;
    const target = createdTarget(item, options);
    if (!target) continue;
    const kind = objectKindForTool(item.name);
    const title = resolveRowTitle(item, target, kind, items, options);
    const id = objectIdentity(target);
    objects.set(id, {
      id,
      title,
      target,
      kind,
      subtitle: kind ? deriveObjectSubtitle(item, kind, options.intl) : undefined,
      createdAt: item.resultCreatedAt ?? item.createdAt,
      initials: kind === 'patient' ? deriveInitials(title) : undefined,
    });
  }
  return [...objects.values()];
};
